import React, { useEffect, useState, useRef } from 'react';
import { safeGet, safeSet, safeRemove, safeJsonParse, getSafeSession } from '../utils/browserSafe';
import baseURL from '../url';
import './Products.css';
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ProductosLoading from '../ProductosLoading/ProductosLoading';
import { Link as Anchor } from "react-router-dom";
import moneda from '../moneda';
import { fetchGeoInfo, getGeoCountryParam, productWithDisplayPrice, filterProductByVisibility } from '../Geo/geoPricing';

SwiperCore.use([Navigation, Pagination, Autoplay]);

function animateFlyToCart(imageSrc, sourceElement) {
    const cartButton = document.querySelector('.cartIconFixed');
    if (!cartButton || !sourceElement) return;

    const sourceRect = sourceElement.getBoundingClientRect();
    const cartRect = cartButton.getBoundingClientRect();

    const flyer = document.createElement('img');
    flyer.src = imageSrc || '';
    flyer.className = 'flyToCartClone';
    flyer.style.left = `${sourceRect.left + sourceRect.width / 2 - 25}px`;
    flyer.style.top = `${sourceRect.top + sourceRect.height / 2 - 25}px`;
    document.body.appendChild(flyer);

    requestAnimationFrame(() => {
        flyer.style.transform = `translate(${cartRect.left - sourceRect.left}px, ${cartRect.top - sourceRect.top}px) scale(0.2)`;
        flyer.style.opacity = '0.2';
    });

    setTimeout(() => {
        if (flyer.parentNode) flyer.parentNode.removeChild(flyer);
    }, 700);
}

export default function Products() {
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fixedCategories, setFixedCategories] = useState(false);
    const categoriasRefs = useRef([]);
    const categoriasInputRef = useRef(null);
    const [productos, setProductos] = useState([]);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('Todo');
    const [animatingProductId, setAnimatingProductId] = useState(null);
    const [geoInfo, setGeoInfo] = useState(null);

    const handleClickCategoria = (categoria) => {
        setCategoriaSeleccionada(categoria);
    };

    useEffect(() => {
        fetchGeoInfo().then((info) => {
            setGeoInfo(info);
            cargarProductos(info);
        });
        cargarCategorias();

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleScroll = () => {
        if (categoriasInputRef.current) {
            if (window.scrollY > categoriasInputRef.current.offsetTop) {
                setFixedCategories(true);
            } else {
                setFixedCategories(false);
            }
        }
    };

    const cargarProductos = (geo = geoInfo) => {
        const countryParam = geo?.countryCode ? `?country=${encodeURIComponent(geo.countryCode)}` : getGeoCountryParam();
        fetch(`${baseURL}/productosGet.php${countryParam}`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                const list = (data.productos || [])
                    .filter(item => filterProductByVisibility(item, geo))
                    .map(item => {
                        const p = productWithDisplayPrice(item, geo);
                        const isCuba = geo?.isCuba !== false;
                        const prevMn = Number(item.precioAnterior || 0);
                        const ratio = (Number(item.precioMn || 0) > 0 && Number(item.precioUsd || 0) > 0)
                            ? (Number(item.precioMn) / Number(item.precioUsd))
                            : 1;
                        if (prevMn > 0) {
                            p.precioAnteriorDisplay = isCuba
                                ? (prevMn * 1.02).toFixed(2)
                                : ((ratio > 0 ? (prevMn / ratio) : prevMn) * 1.02).toFixed(2);
                        } else {
                            p.precioAnteriorDisplay = item.precioAnterior;
                        }
                        return p;
                    });
                setProductos(list);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error al cargar productos:', error);
                setLoading(false);
            });
    };

    const cargarCategorias = () => {
        fetch(`${baseURL}/categoriasGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                const cats=data.categorias||[]; setCategorias([...cats,{idCategoria:'BESTSELLERS',categoria:'Más Vendidos'}]);
            })
            .catch(error => console.error('Error al cargar categorías:', error));
    };

    const fixURL = (url) => {
        if (!url) return null;
        return url.replace('.com.', '.com/');
    };

    const obtenerImagen = (item) => {
        return fixURL(
            item.imagen1 || item.imagen2 || item.imagen3 || item.imagen4
        );
    };

    const addToCartQuick = (e, producto) => {
        e.preventDefault();
        e.stopPropagation();

        if (!producto) return;

        if (Number(producto.stockEnabled || 0) === 1 && Number(producto.stockQty || 0) < 1) {
            toast.error('No hay stock', { autoClose: 700 });
            return;
        }

        const cart = safeJsonParse(safeGet('cart','[]'), []);
        const existingItemIndex = cart.findIndex(item => item.idProducto === producto.idProducto);

        if (existingItemIndex !== -1) {
            const existingItem = cart[existingItemIndex];
            cart[existingItemIndex] = {
                ...existingItem,
                cantidad: (existingItem.cantidad || 1) + 1,
                item: existingItem.item || []
            };
        } else {
            cart.push({
                idProducto: producto.idProducto,
                item: [],
                cantidad: 1
            });
        }

        safeSet('cart', JSON.stringify(cart));
        window.dispatchEvent(new Event('cartUpdated'));
        window.dispatchEvent(new Event('cartBounce'));

        const imageEl = e.currentTarget.closest('a')?.querySelector('img');
        animateFlyToCart(obtenerImagen(producto), imageEl || e.currentTarget);

        setAnimatingProductId(producto.idProducto);
        setTimeout(() => setAnimatingProductId(null), 350);

        // Sin toast de éxito para evitar el conflicto que rompe react-toastify en esta instalación.
    };

    const isOutOfStock = (item) => Number(item?.stockEnabled || 0) === 1 && Number(item?.stockQty || 0) < 1;

    const bestSellers = (productos || [])
        .filter(item => Number(item?.activo ?? 1) === 1 && Number(item?.salesCount || 0) > 0)
        .sort((a, b) => Number(b?.salesCount || 0) - Number(a?.salesCount || 0))
        .slice(0, 15);

    const categoriasConProductos = categorias.filter(categoria => {
        if (categoria.idCategoria === 'BESTSELLERS') return bestSellers.length > 0;
        return productos?.some(producto => Number(producto?.activo ?? 1) === 1 && (Array.isArray(producto?.categoriaIds) ? producto.categoriaIds.includes(Number(categoria?.idCategoria)) : Number(producto?.idCategoria) === Number(categoria?.idCategoria)));
    });

    return (
        <div className='ProductsContain'>
            <ToastContainer />
            {productos?.length > 0 && (
                <div className={`categoriasInputs ${fixedCategories ? 'fixed' : ''}`} ref={categoriasInputRef}>
                    <input
                        type="button"
                        value="Todo"
                        onClick={() => handleClickCategoria('Todo')}
                        style={{
                            backgroundColor: categoriaSeleccionada === 'Todo' ? '#F80050' : '',
                            color: categoriaSeleccionada === 'Todo' ? '#fff' : '',
                            borderBottom: categoriaSeleccionada === 'Todo' ? '2px solid #F80050' : 'none'
                        }}
                    />
                    {categoriasConProductos.map(({ categoria, idCategoria }) => (
                        <input
                            key={idCategoria}
                            type="button"
                            value={categoria}
                            onClick={() => handleClickCategoria(idCategoria)}
                            style={{
                                backgroundColor: categoriaSeleccionada === idCategoria ? '#F80050' : '',
                                color: categoriaSeleccionada === idCategoria ? '#fff' : '',
                                borderBottom: categoriaSeleccionada === idCategoria ? '2px solid #F80050' : 'none'
                            }}
                        />
                    ))}
                </div>
            )}

            {loading ? (
                <ProductosLoading />
            ) : (
                <div className='Products'>
                    {categoriaSeleccionada === 'Todo' && (
                        <>
                            {bestSellers.length > 0 && (
                                <div className='categoriSection'>
                                    <Swiper
                                        effect={'slide'}
                                        grabCursor={true}
                                        slidesPerView={'auto'}
                                        id='swiper_container_products'
                                        autoplay={{ delay: 3000 }}
                                    >
                                        {bestSellers.map(item => (
                                            <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products-masvendidos'>
                                                <Anchor className='cardProdcutmasVendido' to={`/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`}>
                                                    <img src={obtenerImagen(item)} alt="imagen" />
                                                    {isOutOfStock(item) && <span className="stockOut">Agotado</span>}
                                                    {Number(item.stockEnabled || 0) === 1 && Number(item.stockQty || 0) <= Number(item.lowStockAlert || 3) && Number(item.stockQty || 0) > 0 && <span className="stockHot">🔥 Quedan {item.stockQty}</span>}
                                                    <h6 className='masVendido'>Más Vendido</h6>
                                                    <div className='cardText cardTextVertical'>
                                                        <h4 className='productCardTitle' title={item.titulo}>{item.titulo}</h4>
                                                        <span className='productCardDesc'>{item.descripcion}</span>
                                                        <div className='deFLexPrice'>
                                                            <div className='pricesWrap'>
                                                                <h5>{moneda} {item?.precio} <small className='currencyTag'>{item?.precioModo === 'USD' ? 'USD' : 'CUP'}</small></h5>
                                                                {(item.precioAnterior >= 1 && item.precioAnterior !== undefined) && (
                                                                    <h5 className='precioTachado'>{moneda} {item?.precioAnteriorDisplay || item?.precioAnterior} <small className='currencyTag old'>{item?.precioModo === 'USD' ? 'USD' : 'CUP'}</small></h5>
                                                                )}
                                                            </div>
                                                            <button
                                                                className={`quickAddCartBtn ${isOutOfStock(item) ? 'quickAddCartBtnDisabled' : ''} ${animatingProductId === item.idProducto ? 'quickAddCartBtnPulse quickAddCartBtnSuccess' : ''}`}
                                                                disabled={isOutOfStock(item)}
                                                                onClick={(e) => addToCartQuick(e, item)}
                                                                aria-label='Agregar al carrito'
                                                                title='Agregar al carrito'
                                                                type='button'
                                                            >
                                                                {isOutOfStock(item) ? 'Agotado' : <>+ <FontAwesomeIcon icon={faCartShopping} /></>}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </Anchor>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            )}

                            {categoriasConProductos?.map(({ categoria, idCategoria }) => (
                                <div
                                    key={idCategoria}
                                    className='categoriSection'
                                    ref={ref => categoriasRefs.current[categorias.findIndex(cat => cat.idCategoria === idCategoria)] = ref}
                                >
                                    <div className='deFlexTitlesection'>
                                        <h3>{categoria}</h3>
                                        <button onClick={() => handleClickCategoria(idCategoria)}>
                                            Ver más
                                        </button>
                                    </div>

                                    <Swiper
                                        effect={'slide'}
                                        grabCursor={true}
                                        slidesPerView={'auto'}
                                        id='swiper_container_products'
                                    >
                                        {productos?.filter(item => item.idCategoria === idCategoria).map(item => (
                                            <SwiperSlide id='SwiperSlide-scroll-products' key={item.idProducto}>
                                                <Anchor className='cardProdcut cardProdcutVertical' to={`/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`}>
                                                    <img src={obtenerImagen(item)} alt="imagen" />
                                                    {isOutOfStock(item) && <span className="stockOut">Agotado</span>}
                                                    {Number(item.stockEnabled || 0) === 1 && Number(item.stockQty || 0) <= Number(item.lowStockAlert || 3) && Number(item.stockQty || 0) > 0 && <span className="stockHot">🔥 Quedan {item.stockQty}</span>}
                                                    <div className='cardText cardTextVertical'>
                                                        <h4 className='productCardTitle' title={item.titulo}>{item.titulo}</h4>
                                                        <span className='productCardDesc'>{item.descripcion}</span>
                                                        <div className='deFLexPrice'>
                                                            <div className='pricesWrap'>
                                                                <h5>{moneda} {item?.precio} <small className='currencyTag'>{item?.precioModo === 'USD' ? 'USD' : 'CUP'}</small></h5>
                                                                {(item.precioAnterior >= 1 && item.precioAnterior !== undefined) && (
                                                                    <h5 className='precioTachado'>{moneda} {item?.precioAnteriorDisplay || item?.precioAnterior} <small className='currencyTag old'>{item?.precioModo === 'USD' ? 'USD' : 'CUP'}</small></h5>
                                                                )}
                                                            </div>
                                                            <button
                                                                className={`quickAddCartBtn ${isOutOfStock(item) ? 'quickAddCartBtnDisabled' : ''} ${animatingProductId === item.idProducto ? 'quickAddCartBtnPulse quickAddCartBtnSuccess' : ''}`}
                                                                disabled={isOutOfStock(item)}
                                                                onClick={(e) => addToCartQuick(e, item)}
                                                                aria-label='Agregar al carrito'
                                                                title='Agregar al carrito'
                                                                type='button'
                                                            >
                                                                {isOutOfStock(item) ? 'Agotado' : <>+ <FontAwesomeIcon icon={faCartShopping} /></>}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </Anchor>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            ))}
                        </>
                    )}

                    <div className='categoriSectionSelected'>
                        {productos
                            ?.filter(item => {
 const active = Number(item.estado ?? item.activo ?? 1) === 1;
 if(!active) return false;
 if(categoriaSeleccionada==='BESTSELLERS') return Number(item.salesCount || 0) > 0;
 return categoriaSeleccionada !== 'Todo' && (Array.isArray(item.categoriaIds) ? item.categoriaIds.includes(Number(categoriaSeleccionada)) : Number(item.idCategoria) === Number(categoriaSeleccionada));
})
?.sort((a,b)=> Number(b.salesCount||0)-Number(a.salesCount||0))
?.slice(0, categoriaSeleccionada==='BESTSELLERS' ? 50 : 999)
                            ?.map(item => (
                                <Anchor
                                    key={item.idProducto}
                                    className='selectedGridItem'
                                    to={`/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`}
                                >
                                    <div className='cardProdcutSelected cardProdcutSelectedVertical'>
                                        <img src={obtenerImagen(item)} alt="imagen" />
                                        {isOutOfStock(item) && <span className="stockOut">Agotado</span>}
                                        {Number(item.stockEnabled || 0) === 1 && Number(item.stockQty || 0) <= Number(item.lowStockAlert || 3) && Number(item.stockQty || 0) > 0 && <span className="stockHot">🔥 Quedan {item.stockQty}</span>}
                                        <div className='cardTextSelected cardTextVertical'>
                                            <h4 className='productCardTitle' title={item.titulo}>{item.titulo}</h4>
                                            <span className='productCardDesc'>{item.descripcion}</span>
                                            <div className='deFLexPrice'>
                                                <div className='pricesWrap'>
                                                    <h5>{moneda} {item?.precio} <small className='currencyTag'>{item?.precioModo === 'USD' ? 'USD' : 'CUP'}</small></h5>
                                                    {(item.precioAnterior >= 1 && item.precioAnterior !== undefined) && (
                                                        <h5 className='precioTachado'>{moneda} {item?.precioAnteriorDisplay || item?.precioAnterior} <small className='currencyTag old'>{item?.precioModo === 'USD' ? 'USD' : 'CUP'}</small></h5>
                                                    )}
                                                </div>
                                                <button
                                                    className={`quickAddCartBtn ${isOutOfStock(item) ? 'quickAddCartBtnDisabled' : ''} ${animatingProductId === item.idProducto ? 'quickAddCartBtnPulse quickAddCartBtnSuccess' : ''}`}
                                                    disabled={isOutOfStock(item)}
                                                    onClick={(e) => addToCartQuick(e, item)}
                                                    aria-label='Agregar al carrito'
                                                    title='Agregar al carrito'
                                                    type='button'
                                                >
                                                    {isOutOfStock(item) ? 'Agotado' : <>+ <FontAwesomeIcon icon={faCartShopping} /></>}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Anchor>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
}
